/**
 * Public status page (Sprint 87 — DevOps).
 *
 * Rendered at /status (no auth). Consumes GET /api/status and shows:
 *   • Overall health pill (Operational / Degraded)
 *   • Per-service status rows (Mongo · Cloudinary · Razorpay · Couriers · WhatsApp)
 *   • Last 30 days of incidents (from db.alerts)
 *   • Auto-refresh every 30 seconds
 *
 * Built so B2B prospects can see real-time uptime — common ask on sales calls.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, Activity, ServerCrash } from "lucide-react";
import api from "../services/api";

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/status");
        if (!cancelled) {
          setData(data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load status");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const isUp = data?.overall === "operational";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Activity className="h-8 w-8 text-[#FF6B00]" />
          <h1 className="text-3xl font-bold text-slate-900">PickNGo System Status</h1>
        </div>
        <p className="text-sm text-slate-500 mb-8">Real-time health of every dependency. Auto-refreshes every 30 s.</p>

        {/* Overall status banner */}
        {loading && !data && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 flex items-center gap-3" data-testid="status-loading">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            <p className="text-sm text-slate-600">Checking system health…</p>
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 flex items-center gap-3">
            <ServerCrash className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">Could not reach status endpoint: {error}</p>
          </div>
        )}
        {data && (
          <div
            className={`rounded-xl p-6 mb-6 ${isUp ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}
            data-testid="status-overall"
          >
            <div className="flex items-center gap-3">
              {isUp ? (
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-7 w-7 text-amber-600" />
              )}
              <div className="flex-1">
                <p className={`text-xl font-bold ${isUp ? "text-emerald-900" : "text-amber-900"}`}>
                  {isUp ? "All systems operational" : "Service degraded"}
                </p>
                <p className={`text-xs ${isUp ? "text-emerald-700" : "text-amber-700"}`}>
                  Last check: {new Date(data.checked_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Per-service rows */}
        {data?.services && (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="status-services">
            {data.services.map((s, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  {s.ok ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                    {s.note && <p className="text-[11px] text-slate-500 truncate">{s.note}</p>}
                    {s.error && <p className="text-[11px] text-red-500 truncate">{s.error}</p>}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  {s.latency_ms != null && (
                    <span className="text-xs text-slate-500 tabular-nums">{s.latency_ms} ms</span>
                  )}
                  {s.mode && (
                    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${s.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {s.mode}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Incidents */}
        <h2 className="text-lg font-semibold text-slate-900 mt-10 mb-3">Last 30 days</h2>
        {data?.incidents_last_30d?.length === 0 && (
          <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800" data-testid="no-incidents">
            ✓ No incidents reported in the last 30 days.
          </p>
        )}
        {data?.incidents_last_30d?.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="incidents">
            {data.incidents_last_30d.map((inc, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className={`shrink-0 h-2 w-2 rounded-full ${inc.severity === "fatal" ? "bg-red-500" : "bg-amber-500"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 truncate">{inc.message}</p>
                  <p className="text-[11px] text-slate-500">
                    {new Date(inc.at).toLocaleString()} · {inc.kind} · {inc.severity}
                  </p>
                </div>
                {inc.resolved && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Resolved</span>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 mt-10 text-center">
          PickNGo Express Logistics · Status page powered by an internal probe + last 30 days of monitored events.
        </p>
      </div>
    </div>
  );
}
